// @ts-nocheck
import { useState } from "react";
import { PlannedMealCalendar } from "@corpuscomplete/core-ui/nutrition/planned-meal/calendar";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Card } from "@corpuscomplete/web-ui/components/card";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { PlusIcon } from "lucide-react";
import { PlannedMealCard } from "./planned-meal-card";

export function PlannedMealsDateRow({
  compact = false,
  memberUserIds,
  onSelectEntryKey,
  selectedEntryKey,
  readOnly,
  row,
}: {
  compact?: boolean;
  memberUserIds: readonly string[];
  onSelectEntryKey: (key: string) => void;
  selectedEntryKey: string | null;
  readOnly: boolean;
  row: PlannedMealCalendar.DateRow;
}) {
  const defaults = PlannedMealCalendar.useQuickCreateDefaults({ date: row.date, memberUserIds });
  const { createDraft } = PlannedMealCalendar.useMutations();
  const [error, setError] = useState<string | null>(null);
  const disabledReason = PlannedMealCalendar.quickCreateDisabledReason({ readOnly, defaults });

  const handleAdd = () => {
    setError(null);
    if (readOnly || defaults.disabled) {
      setError(disabledReason ?? "Cannot add meal");
      return;
    }
    void createDraft(defaults.payload)
      .client.then((result) => {
        if (result.type === "error") {
          setError(result.error.message);
        }
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : "Cannot add meal");
      });
    onSelectEntryKey(
      PlannedMealCalendar.entryKeyFromParts(
        defaults.payload.events?.[0]?.id ??
          defaults.payload.instances?.[0]?.id ??
          defaults.payload.id,
        row.date,
      ),
    );
  };

  return (
    <Card className="group/date-row gap-2 p-3" size="sm">
      <div className="flex items-center gap-2">
        <div className="mr-auto flex flex-col">
          <Text className="font-medium">
            {row.date.toLocaleString("en-US", { weekday: "long", month: "short", day: "numeric" })}
          </Text>
          <Text variant="xs" className="text-muted-foreground">
            {row.entries.length === 0 ? "No planned meals" : `${row.entries.length} planned`}
          </Text>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="opacity-100 sm:opacity-0 sm:group-hover/date-row:opacity-100 sm:group-focus-within/date-row:opacity-100"
          disabled={!!disabledReason}
          title={disabledReason ?? undefined}
          onClick={handleAdd}
        >
          <PlusIcon className="size-3" />
          Add Meal
        </Button>
      </div>
      {error && <Text className="text-destructive">{error}</Text>}
      <div className={compact ? "grid gap-2" : "grid gap-2 sm:grid-cols-2 lg:grid-cols-3"}>
        {row.events.map((entry) => (
          <PlannedMealCard
            key={PlannedMealCalendar.entryKey(entry)}
            entry={entry}
            selected={selectedEntryKey === PlannedMealCalendar.entryKey(entry)}
            onSelect={() => onSelectEntryKey(PlannedMealCalendar.entryKey(entry))}
          />
        ))}
      </div>
      {row.entries.length === 0 && (
        <Text variant="xs" className="text-muted-foreground">
          Use Add Meal to draft a planned meal for this date.
        </Text>
      )}
    </Card>
  );
}
